import { EnumSurveyQuestionType } from "gql/generated";
import {
  flattenSurveyQuestionGroups,
  groupSurveyQuestions,
  isToggleQuestionType,
} from "shared/components/organisms/SurveyQuestionInput/surveyQuestionVisibility";
import { isSurveyDesignFieldType } from "shared/components/organisms/SurveyQuestionInput/surveyQuestionDesign";

export type GroupableQuestion = {
  _id: string;
  type: EnumSurveyQuestionType | string;
  parentQuestion?: string;
};

export type QuestionDropTarget =
  | { kind: "top"; overId: string }
  | { kind: "group"; groupId: string; overChildId?: string };

/** Toggles and design fields always stay top-level. */
export const canJoinQuestionGroup = (question: GroupableQuestion) =>
  !isToggleQuestionType(question.type) &&
  !isSurveyDesignFieldType(question.type);

/**
 * Drops follow-up links that are no longer valid (missing or non-toggle
 * parent, or a question that can't be a follow-up) and orders the list so
 * every follow-up sits directly after its toggle.
 */
export const normalizeQuestionGroups = <T extends GroupableQuestion>(
  questions: T[],
): T[] => {
  const toggleIds = new Set(
    questions.filter(q => isToggleQuestionType(q.type)).map(q => q._id),
  );
  const cleaned = questions.map(q =>
    q.parentQuestion &&
    (!toggleIds.has(q.parentQuestion) || !canJoinQuestionGroup(q))
      ? { ...q, parentQuestion: undefined }
      : q,
  );
  return flattenSurveyQuestionGroups(groupSurveyQuestions(cleaned));
};

/**
 * Moves a question (a toggle moves with its follow-ups) to a drop target.
 * Moving down lands after the target, moving up lands before it, matching
 * how a flat sortable list behaves.
 */
export const moveQuestionToTarget = <T extends GroupableQuestion>(
  questions: T[],
  activeId: string,
  target: QuestionDropTarget,
): T[] => {
  const flat = normalizeQuestionGroups(questions);
  const active = flat.find(q => q._id === activeId);
  if (!active) return questions;

  const resolvedTarget: QuestionDropTarget =
    target.kind === "group" &&
    (!canJoinQuestionGroup(active) || target.groupId === activeId)
      ? { kind: "top", overId: target.groupId }
      : target;
  const overId =
    resolvedTarget.kind === "top"
      ? resolvedTarget.overId
      : resolvedTarget.overChildId;
  if (overId === activeId) return questions;

  const block = isToggleQuestionType(active.type)
    ? flat.filter(q => q._id === activeId || q.parentQuestion === activeId)
    : [active];
  const blockIds = new Set(block.map(q => q._id));
  if (overId && blockIds.has(overId)) return questions;

  const movedActive: T = {
    ...active,
    parentQuestion:
      resolvedTarget.kind === "group" ? resolvedTarget.groupId : undefined,
  };
  const moving = block.map(q => (q._id === activeId ? movedActive : q));
  const rest = flat.filter(q => !blockIds.has(q._id));
  const activeIndex = flat.findIndex(q => q._id === activeId);

  let insertAt: number;
  if (resolvedTarget.kind === "group" && !resolvedTarget.overChildId) {
    const lastInGroup = rest.reduce(
      (last, q, i) =>
        q._id === resolvedTarget.groupId ||
        q.parentQuestion === resolvedTarget.groupId
          ? i
          : last,
      -1,
    );
    if (lastInGroup < 0) return questions;
    insertAt = lastInGroup + 1;
  } else {
    const overIndex = rest.findIndex(q => q._id === overId);
    if (overIndex < 0) return questions;
    const movingDown = activeIndex < flat.findIndex(q => q._id === overId);
    if (!movingDown) {
      insertAt = overIndex;
    } else if (resolvedTarget.kind === "top") {
      // Skip past the whole group when landing after a toggle.
      let end = overIndex + 1;
      while (end < rest.length && rest[end].parentQuestion === overId) end++;
      insertAt = end;
    } else {
      insertAt = overIndex + 1;
    }
  }

  return normalizeQuestionGroups([
    ...rest.slice(0, insertAt),
    ...moving,
    ...rest.slice(insertAt),
  ]);
};
